import { PromptExtractionResult, Score } from "./aiOutput";
import { AuthUser } from "./auth";
import { IContractCategory } from "./contractCategory";
import { IDatePeriod } from "./datePeriod";
import { ISignature } from "./signature";
import { IVendor } from "./vendor";

export enum ContractStatus {
  DRAFT = "DRAFT",
  PUBLISHED = "PUBLISHED",
  SIGNING = "SIGNING",
  SIGNED = "SIGNED",
  ACTIVE = "ACTIVE",
  ARCHIVED = "ARCHIVED",
  EXPIRED = "EXPIRED",
  ALL = "ALL",
}

export type IContractDates = {
  startDate?: string; // ISO format, e.g. 2023-09-15T00:00:00.000Z
  endDate?: string; // ISO format, e.g. 2023-09-15T00:00:00.000Z
  signatureDate?: string; // ISO format, e.g. 2023-09-15T00:00:00.000Z
  explanation?: string;
  context?: ISpykioContext[]; // spykio context
};

export type IContractNoticePeriod = IDatePeriod & { explanation?: string } & {
  context?: ISpykioContext[];
};

export type IContractFinancials = {
  totalValue?: number;
  amount?: number; // this is old, but need to keep for older references
  currency?: string;
  paymentTerms?: string;
  explanation?: string;
  context?: ISpykioContext[]; // spykio context
};

export type ITask = {
  title?: string;
  body?: string;
  dueDate?: string;
  explanation?: string;
};

export type IContractTask = {
  details?: ITask;
  context?: ISpykioContext[]; // spykio context
  logicalCheck?: ILogicalCheck;
};

export type IContractDeviation = {
  details?: Score;
  context?: ISpykioContext[]; // spykio context
  logicalCheck?: ILogicalCheck;
};

export type LifecycleChange = {
  changeDate?: string; // ISO format, e.g. 2023-09-15T00:00:00.000Z
  purpose?: "renewal" | "amendment" | "termination" | "other"; // purpose of the change
  newStartDate?: string; // ISO format, e.g. 2023-09-15T00:00:00.000Z
  newEndDate?: string; // ISO format, e.g. 2023-09-15T00:00:00.000Z
  signatureDate?: string; // ISO format, e.g. 2023-09-15T00:00:00.000Z
  signedBy?: string[]; // names of the people who signed the contract
  summary?: string; // summary of the change
  referenceDocumentId?: string; // ID of the document that contains the change
};

export type IContractLifecycle = {
  initial?: {
    startDate?: string; // ISO format, e.g. 2023-09-15T00:00:00.000Z
    endDate?: string; // ISO format, e.g. 2023-09-15T00:00:00.000Z
    signatureDate?: string; // ISO format, e.g. 2023-09-15T00:00:00.000Z
    signedBy?: string[]; // names of the people who signed the contract
    summary?: string; // summary of the contract
  };
  changes?: LifecycleChange[];
};

export type IContract = {
  id: string;
  name: string;
  subject: string;
  type?: string;
  category?: string;
  procurement?: {
    category: string;
    explanation: string;
  };
  contractCategory?: IContractCategory;

  folderIds?: string[];
  summary: string;
  countryOfJurisdiction: string;
  vendorId: string; // for creating a link to the Vendors container
  vendorName: string; // for displaying the vendor name at the moment of contract creation
  vendorAddress: string; // for displaying the vendor address at the moment of contract creation
  // category: string;

  seller: {
    // the party that promises to do something, e.g. deliver a product, provide a service, etc. for the other party, typically a vendor or service provider
    name: string;
    address: string;
    contactPersons: {
      name: string;
      email: string;
      phone: string;
      role: string;
    }[];
    explanation: string; // in case a clear promisor is not specified in the contract, use this field to describe the promisor
  };
  buyer: {
    // the party that receives the promise, e.g. the product or service from the other party, typically a customer or client
    name: string;
    address: string;
    contactPersons: {
      name: string;
      email: string;
      phone: string;
      role: string;
    }[];
    explanation: string; // in case a clear promisee is not specified in the contract, use this field to describe the promisee
  };

  prompts?: PromptExtractionResult[];

  exludedPromptIds?: string[];

  dates?: IContractDates & { logicalCheck?: ILogicalCheck };

  noticePeriod?: IContractNoticePeriod & {
    logicalCheck?: ILogicalCheck;
  };

  budget?: IContractFinancials & { logicalCheck?: ILogicalCheck };

  vendor?: IVendor & {
    context: ISpykioContext[]; // spykio context
    logicalCheck?: ILogicalCheck;
  };

  deviations?: IContractDeviation[];

  lifecycle?: IContractLifecycle;

  tasks?: IContractTask[];

  adminIds: string[];
  ownerIds: string[];
  managerIds: string[];
  readerIds: string[];

  outlookMessageId?: string; // indicated the outlook message the contract was created from (if applicable)
  created: number;
  modified: number;
  tenantId?: string;
  isAnalyzed?: boolean;
  users?: AuthUser[];
  signatures?: ISignature[];

  // recommendation: string;
  completionDate?: string; // completion date of the last analysis in ISO format, e.g. 2023-09-15T00:00:00.000Z
  status?: ContractStatus;
  analyzeStatus?: AIReadingStatus;

  createdBy?: string;
};

export type ILogicalCheck = {
  logic_check?: {
    is_logical?: boolean;
    explanation?: string;
  };
  errors?: {
    type?: string; // e.g. "content", "structure", "incomplete", "contradiction"
    detail?: string; // exact description of the error or inconsistency
  }[];
  risks?: {
    type?: string; // e.g. "operational", "safety", "bias"
    detail?: string; // description of the risk associated with the output
  }[];
};

export type ISpykioContext = {
  documentId: string; // ID of the document in which the context was found
  contractId: string; // ID of the contract to which the document belongs
  documentName: string; // name of the document
  relevantSections: {
    paragraphIds: string[]; // ID of the paragraph in the document
    text: string; // text of the paragraph
    relevanceScore?: number; // relevance score of the paragraph
    explanation?: string; // explanation of the relevance score
  }[];
};

export enum AIReadingStatus {
  COMPLETED = "COMPLETED",
  ANALYZING = "ANALYZING",
  ERROR = "ERROR",
  STOPPED = "STOPPED",
  NONE = "NONE",
}

// export enum AnalysisStatus {
//   FAILED = "failed",
//   SUCCESS = "success",
//   ANALYZING = "analyzing",
//   NONE = "none",
//   STOPPED = "stopped",
// }

export type AnalyzedContract = {
  status?: ContractStatus;
  name: string;
  contractId: string;
  progress?: number;
  analyzeStatus?: AIReadingStatus;
  analyzeTaskId?: string;
};

export enum LoadingState {
  QUEUED = "QUEUED",
  READING = "READING", // reading the document, by Spykio and Azure Document Intelligence
  DATA_EXTRACTION = "DATA_EXTRACTION", // extracting data from the document, by Spykio AI
  COMPLETED = "COMPLETED",
  FAILED = "FAILED",
}

export enum ExtractionComponent {
  DATES = "DATES",
  NOTICE_PERIOD = "NOTICE_PERIOD",
  FINANCIALS = "FINANCIALS",
  METADATA = "METADATA",
  VENDOR = "VENDOR",
  CONTRACT_CATEGORY = "CONTRACT_CATEGORY",
  TERMS_AND_CONDITIONS = "TERMS_AND_CONDITIONS",
  LIFECYCLE = "LIFECYCLE",
}

export type ContractProgress = {
  contractId: string;
  name: string;
  overallProgress?: number;
  loadingState?: LoadingState;
  loadingStatePerComponent?: Record<ExtractionComponent, LoadingState>;
  loadingStatePerPrompt?: Record<string, LoadingState>;
  initiationDate?: string;
  completionDate?: string;
  documentsProgress?: DocumentProgress[];
};

export type DocumentProgress = {
  documentId: string;
  contractId: string;
  name: string;
  overallProgress?: number;
  loadingState?: LoadingState;
  loadingStatePerComponent?: Record<ExtractionComponent, LoadingState>;
  loadingStatePerPrompt?: Record<string, LoadingState>;
  initiationDate?: string;
  completionDate?: string;
};
